import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy, inject } from '@angular/core';
import { eNormalizedAddressType, eNormalizedPartyType, eOrderChangeOperation } from '../../enums';
import { Utilities } from '../../utilities';
import { OrderChange } from '../../interfaces/order-change';
import { OrderManagerService } from '../../services/order-manager/order-manager.service';
import { Subject, takeUntil } from 'rxjs';
import { ParsedBusinessReportParty } from '../../interfaces/business-report-parsed-results/parsed-business-report-party';
import { ParsedBusinessReportAddress } from '../../interfaces/business-report-parsed-results/parsed-business-report-address';
import { SearchResultsRepositoryService } from '../../services/search-results/search-results-repository.service';
import { Order } from '../../interfaces/orders/order';
import { TranslocoService } from '@jsverse/transloco';
import { EnumLabelService } from '../../services/enum-label/enum-label.service';

interface AddressChangeEntry { index: number, address: ParsedBusinessReportAddress, changes: OrderChange[] }
interface PartyChangeEntry { index: number, party: ParsedBusinessReportParty, changes: OrderChange[], addressChanges: AddressChangeEntry[] }

@Component({
    selector: 'lib-ubo-change-details',
    templateUrl: './ubo-change-details.component.html',
    styleUrls: [
        './ubo-change-details.component.css',
        './../../../results-page.css'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UboChangeDetailsComponent implements OnInit, OnDestroy {
  private readonly enumLabels = inject(EnumLabelService);
  private readonly transloco = inject(TranslocoService);
  @Input() public order: Order | undefined;
  @Input() admin: boolean = false;

  protected shouldShowMaterialChangeAlert = false;

  protected addressFieldChangesByAddress: AddressChangeEntry[] = [];
  protected addRemoveAddresses = new Map<OrderChange, ParsedBusinessReportAddress>();
  protected partyFieldChangesByParty: PartyChangeEntry[] = [];
  protected addRemoveParties = new Map<OrderChange, ParsedBusinessReportParty>();

  private onDestroy$ = new Subject<void>();

  constructor(
    private orderManager: OrderManagerService,
    private searchResultsService: SearchResultsRepositoryService) { }

  ngOnInit(): void {
    if(!this.order) {
      this.orderManager.currentOrder.pipe(
        takeUntil(this.onDestroy$)
      ).subscribe(order => this.handleOrder(order));
    } else {
      this.handleOrder();
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  hasChanges(): boolean {
    return (this.order?.orderChanges?.length ?? 0) > 0;
  }

  isAdditionChange(change: OrderChange): boolean {
    return change.operation === eOrderChangeOperation.Add;
  }

  isRemovalChange(change: OrderChange): boolean {
    return change.operation === eOrderChangeOperation.Remove;
  }

  isReplacementChange(change: OrderChange): boolean {
    return change.operation === eOrderChangeOperation.Replace;
  }

  trimFirstSlash(path: string): string {
    if (path.charAt(0) === '/') {
      return path.substring(1);
    } else {
      return path;
    }
  }

  spaceCase(val: string) {
    return Utilities.spacePascalCase(val);
  }

  getAddressType(type: eNormalizedAddressType | undefined) {
    if (!type || type === eNormalizedAddressType.none) {
      return this.transloco.translate('common.labels.address');
    } else {
      return this.enumLabels.label('normalizedAddressType', type);
    }
  }

  hasChange(entry: { changes: OrderChange[] }, field: string): boolean {
    return entry.changes.some(c => this.fieldEndsWithPath(c.path, field));
  }

  getFirstChangeNewFullValue(entry: { changes: OrderChange[] }) {
    const change = entry.changes.at(0);
    return !change ? '' : change.newFullValue;
  }

  getFirstChangeOriginalFullValue(entry: { changes: OrderChange[] }) {
    const change = entry.changes.at(0);
    return !change ? '' : change.originalFullValue;
  }

  getNewValue(entry: { changes: OrderChange[] }, field: string): string {
    const change = entry.changes.find(c => this.fieldEndsWithPath(c.path, field));
    return !change ? '' : change.newValue;
  }

  getOriginalValue(entry: { changes: OrderChange[] }, field: string): string {
    const change = entry.changes.find(c => this.fieldEndsWithPath(c.path, field));
    return !change ? '' : change.originalValue;
  }

  fieldEndsWithPath(path: string, field: string) {
    const splitPath = path.toLowerCase().split('/');
    const index = splitPath.length - 1;
    return splitPath.at(index) === field.toLowerCase();
  }

  groupAddressFieldChangesByAddress(addresses: ParsedBusinessReportAddress[], changes: OrderChange[], pathIndex: number): AddressChangeEntry[] {
    const result: AddressChangeEntry[] = [];

    changes.forEach(c => {
      const splitPath = this.trimFirstSlash(c.path)?.split('/');
      const index = +(splitPath.at(pathIndex) ?? 99999);
      const address = addresses.at(index);

      if (!address) {
        return;
      }

      let item = result.find(entry => entry.index === index);

      if (!item) {
        item = {
          index: index,
          address: address,
          changes: [c]
        };

        result.push(item);
      } else {
        item.changes.push(c);
      }
    });

    return result;
  }

  getIconTooltip(op: string) {
    switch (op.toLowerCase()) {
      case 'add':
        return this.transloco.translate('uboChangeDetails.addedTooltip');
      case 'remove':
        return this.transloco.translate('uboChangeDetails.removedTooltip');
      case 'replace':
        return this.transloco.translate('uboChangeDetails.replacedTooltip');
      default:
        return '';
    }
  }
  
  handleOrder(order?: Order) {
    if (order) {
      this.order = order;
    }

    if (!this.order) {
      return;
    }

    if ((this.order.orderChanges?.length ?? -1) > 0) {
      this.searchResultsService.getBusinessReportParsedResults(this.order.orderChanges?.at(-1)?.originalOrderID ?? '').pipe(
        takeUntil(this.onDestroy$)
      ).subscribe(governmentResults => {
        // Manipulating address changes here
        const addressChanges = this.order?.orderChanges?.filter(c => this.trimFirstSlash(c.path).toLowerCase().startsWith('address')) ?? [];
        this.addressFieldChangesByAddress = this.groupAddressFieldChangesByAddress(governmentResults.addresses, addressChanges, 1);

        addressChanges?.filter(c => c.operation === eOrderChangeOperation.Add).forEach(c => {
          const address = JSON.parse(c.newValue) as ParsedBusinessReportAddress;
          this.addRemoveAddresses.set(c, address);
        });

        addressChanges?.filter(c => c.operation === eOrderChangeOperation.Remove).forEach(c => {
          const address = JSON.parse(c.originalValue) as ParsedBusinessReportAddress;
          this.addRemoveAddresses.set(c, address);
        });

        // Manipulating party changes here
        const partyChanges = this.order?.orderChanges?.filter(c => this.trimFirstSlash(c.path).toLowerCase().startsWith('parties')) ?? [];

        partyChanges.filter(c => c.operation === eOrderChangeOperation.Replace ||
          (c.path.toLowerCase().includes('addresses') &&
          (c.operation === eOrderChangeOperation.Add ||
          c.operation === eOrderChangeOperation.Remove))
        ).forEach(c => {
          const splitPath = c.path.split('/');
          const index = +(splitPath.at(2) ?? 99999);
          const party = governmentResults.parties.at(index);

          if (!party) {
            return;
          }

          let item = this.partyFieldChangesByParty.find(entry => entry.index === index);

          if (!item) {
            item = {
              index: index,
              party: party,
              changes: [c],
              addressChanges: []
            };

            this.partyFieldChangesByParty.push(item);
          } else {
            item.changes.push(c);
          }

          const isAddressChange = c.path.toLowerCase().includes('addresses');
          let newIndex = 10000;

          if (isAddressChange) {
            if (c.operation === eOrderChangeOperation.Add ||
              c.operation === eOrderChangeOperation.Remove) {
              item.addressChanges.push({
                index: newIndex++,
                address: c.operation === eOrderChangeOperation.Add ? JSON.parse(c.newValue) : JSON.parse(c.originalValue),
                changes: [c]
              });
            } else {
              const index = +(splitPath.at(4) ?? 99999);
              const address = item.party.addresses.at(index);

              if (!address) {
                return;
              }

              let addressItem = item.addressChanges.find(entry => entry.index === index);

              if (!addressItem) {
                addressItem = {
                  index: index,
                  address: address,
                  changes: [c]
                };

                item.addressChanges.push(addressItem);
              } else {
                addressItem.changes.push(c);
              }
            }
          }
        });

        partyChanges?.filter(c => !c.path.toLowerCase().includes('addresses') && c.operation === eOrderChangeOperation.Add).forEach(c => {
          const party = JSON.parse(c.newValue) as ParsedBusinessReportParty;
          this.addRemoveParties.set(c, party);
        });

        partyChanges?.filter(c => !c.path.toLowerCase().includes('addresses') && c.operation === eOrderChangeOperation.Remove).forEach(c => {
          const party = JSON.parse(c.originalValue) as ParsedBusinessReportParty;
          this.addRemoveParties.set(c, party);
        });
      })
    }
  }
}